import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, XCircle, Lock } from 'lucide-react';

import { API } from '@/utils/api';

export default function SetNewPassword() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Validate token on mount
    const validateToken = async () => {
      try {
        await axios.get(`${API}/auth/reset-password/${token}`);
        setValid(true);
      } catch {
        setValid(false);
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('setPassword.passwordsMismatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('setPassword.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password/${token}`, { password });
      // Soft-signup welcome flow: backend zwraca token JWT — auto-login
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        if (response.data?.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      }
      setSuccess(true);
      toast.success(t('setPassword.success'));
      // Welcome flow → bezpośrednio do panelu hodowcy
      const isWelcome = new URLSearchParams(window.location.search).get('welcome') === '1';
      const target = response.data?.token ? '/panel' : '/login';
      setTimeout(() => navigate(isWelcome ? '/panel' : target), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('setPassword.changeError'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{t('setPassword.linkExpired')}</h1>
          <p className="text-zinc-400 mb-6">{t('setPassword.linkExpiredDesc')}</p>
          <Link to="/reset-hasla">
            <Button className="bg-blue-500 hover:bg-blue-600">
              {t('setPassword.requestNew')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{t('setPassword.success')}</h1>
          <p className="text-zinc-400">{t('setPassword.redirecting')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo.svg" alt="ApisAI" className="w-10 h-10" />
            <span className="font-semibold text-xl text-white">ApisAI</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">{t('setPassword.title')}</h1>
          <p className="text-zinc-400">{t('setPassword.subtitle')}</p>
        </div>

        <div className="glass-card rounded-3xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">{t('setPassword.newPassword')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-[#0B0F1A]/50 border-white/10 focus:border-blue-500/50 rounded-xl h-12 text-white placeholder:text-zinc-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-300">{t('setPassword.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-[#0B0F1A]/50 border-white/10 focus:border-blue-500/50 rounded-xl h-12 text-white placeholder:text-zinc-500"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  {t('setPassword.submit')}
                </>
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
